import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useSupportEnabled } from "@/hooks/useSupportEnabled";
import { toast } from "sonner";

const TimerContext = createContext(null);

export function TimerProvider({ children }) {
  const { currentOrgId } = useAuth();
  const { enabled } = useSupportEnabled();
  const [timer, setTimer] = useState(null);   // backend-persisted state (server-authoritative)
  const [tick, setTick] = useState(0);         // local display ticker
  const orgRef = useRef(currentOrgId);
  orgRef.current = currentOrgId;

  const refresh = useCallback(async () => {
    if (!orgRef.current || !enabled) { setTimer(null); return null; }
    try {
      const r = await api.get(`/support/timer?org_id=${orgRef.current}`);
      setTimer(r.data?.active ? r.data : null);
      return r.data;
    } catch { setTimer(null); return null; }
  }, [enabled]);

  // Poll server (authoritative) + re-sync on focus; local ticker only drives the display.
  useEffect(() => { refresh(); }, [refresh, currentOrgId]);
  useEffect(() => {
    if (!enabled) return;
    const poll = setInterval(refresh, 15000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(poll); window.removeEventListener("focus", onFocus); };
  }, [enabled, refresh]);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const displaySeconds = (() => {
    if (!timer) return 0;
    let s = timer.elapsed_seconds || 0;
    if (timer.state === "running") {
      const drift = Math.floor((Date.now() - (timer._synced || Date.now())) / 1000);
      s += Math.max(0, drift);
    }
    return s;
  })();
  // stamp sync time when timer changes
  useEffect(() => { if (timer && !timer._synced) setTimer((t) => t ? { ...t, _synced: Date.now() } : t); }, [timer]);

  const start = useCallback(async (payload) => {
    const r = await api.post("/support/timer/start", { org_id: orgRef.current, ...payload });
    await refresh();
    return r.data;
  }, [refresh]);
  const pause = useCallback(async () => { await api.post("/support/timer/pause", { org_id: orgRef.current }); await refresh(); }, [refresh]);
  const resume = useCallback(async () => { await api.post("/support/timer/resume", { org_id: orgRef.current }); await refresh(); }, [refresh]);
  const patch = useCallback(async (payload) => { await api.patch("/support/timer", { org_id: orgRef.current, ...payload }); await refresh(); }, [refresh]);
  const stop = useCallback(async () => {
    const r = await api.post("/support/timer/stop", { org_id: orgRef.current });
    await refresh();
    toast.success("Timer saved as a time entry");
    return r.data;
  }, [refresh]);
  const discard = useCallback(async () => {
    await api.post("/support/timer/discard", { org_id: orgRef.current });
    await refresh();
    toast("Timer discarded");
  }, [refresh]);

  return (
    <TimerContext.Provider value={{ timer, displaySeconds, enabled, refresh, start, pause, resume, patch, stop, discard }}>
      {children}
    </TimerContext.Provider>
  );
}

export const useTimer = () => useContext(TimerContext) || {};
